// import PropTypes from 'prop-types';
import React, { ReactElement, useMemo } from 'react';

import NoSSR from '../../../context/ssr/no-ssr';
import { isClient, isMobile, isServer } from '../../../utils/device-utils';
import { IconCircleLeft, IconCircleRight } from '../../atoms';
import { IIcon } from '../../atoms/icons/icon';
import { IMatchChangeLmp } from '../../molecules/match-change/match-change';
import GliderWrapper, { IGliderWrapper } from '../../organisms/glider-wrapper/glider-wrapper';
import MatchChangeTable, { IMatchChangeTable } from '../../organisms/match-change-table/match-change-table';

export interface IMatchChangeLmpTemplate {
  matchId: string; // Current match to SELECT
  matchList: Array<IMatchChangeLmp>;
  trackingData?: object;
}

// ARROWS (DESKTOP only)
const isClientDesktop: boolean = isServer() ? false : !isMobile() || false;
const iconLeft = (): ReactElement<IIcon> => <IconCircleLeft size="xsmall" color="light" />;
const iconRight = (): ReactElement<IIcon> => <IconCircleRight size="xsmall" color="light" />;

const argsGliderWrapper: IGliderWrapper<IMatchChangeTable> = {
  hasArrows: isClientDesktop,
  draggable: true,
  exactWidth: true,
  iconLeft: isClientDesktop ? iconLeft() : undefined,
  iconRight: isClientDesktop ? iconRight() : undefined,
  itemWidth: 104,
  scrollLock: true,
  Component: MatchChangeTable,
};

// Component to return when SERVER-SIDE (MatchChangeTable only, no Glider)
const ssrPlaceholder = (propsComponent: IMatchChangeTable) => <MatchChangeTable {...propsComponent} isSSR />;

// ISS-6804 - ERROR BOUNDARY (waiting for BE to FIX mandatory data) + if ONLY 1 MATCH (e.g. the current one in LMP) then do NOT return a GLIDER of MATCHES!
const isValidMatchList = (matchList: Array<IMatchChangeLmp>) =>
  matchList.length > 1 &&
  matchList.every(
    (match: IMatchChangeLmp) => match.status && match.link && match.home.shortName && match.away.shortName
  );

const MatchChangeLmp: React.FC<IMatchChangeLmpTemplate> = ({
  /* istanbul ignore next: default value */ matchList = [],
  matchId,
  trackingData,
}) => {
  const transformedMatchList = useMemo(() => {
    if (!isClient()) {
      return matchList;
    }

    const currentUrl = location.href;
    return matchList.map((match) => {
      let link = match.link;
      if (currentUrl.match(/\/lmpappsport/)) {
        link += '/lmpappsport';
      }
      return {
        ...match,
        link,
      };
    });
  }, [matchList]);

  const activeSlideIndex: number = transformedMatchList.findIndex((match: IMatchChangeLmp) => match.id === matchId);
  const propsComponent: IMatchChangeTable = {
    activeSlideIndex,
    variant: 'lmp',
    matchList: transformedMatchList,
    trackingData,
  };

  return isValidMatchList(matchList) ? (
    // SERVER-SIDE should have NO GLIDER rendered! "react-glider" works only in BROWSER!
    <NoSSR placeholder={ssrPlaceholder(propsComponent)}>
      <GliderWrapper {...argsGliderWrapper} propsComponent={propsComponent} />
    </NoSSR>
  ) : null;
};

export default MatchChangeLmp;
